// external
import React, { FC } from 'react';
import { Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TripOriginIcon from '@mui/icons-material/TripOrigin';

// internal
import { format, parseISO } from 'date-fns';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { TrainingCardGroup, TrainingCardTeam } from '../../pages/home/home.page';

// styles
import './coe-training-card-2.component.css';

interface COETrainingCardProps {
  title: string;
  time: string;
  location: string;
  type: 'personal' | 'group' | 'team';
  viewerType: 'coach' | 'athlete' | 'guardian';
  onClick: () => void;
  complete?: boolean;
  plannedDistance?: number;
  plannedDuration?: number;
  completedDistance?: number;
  completedDuration?: number;
  team?: TrainingCardTeam;
  group?: TrainingCardGroup;
  isWatchUpload?: boolean;
  rsvpStatus?: boolean;
  unreadCommentsCount?: number;
  athleteName?: string;
  isSelected?: boolean;
}

const formatDuration = (seconds?: number): string => {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const formatDistance = (distance?: number): string => {
  if (!distance) return '—';
  return `${distance.toFixed(1)} ${getMeasurementUnit()}`;
};

const teamInitials = (name?: string): string =>
  name
    ? name
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?';

const COETrainingCard: FC<COETrainingCardProps> = (props) => {
  const isPlanned = !props.complete;
  const locationLabel = props.location?.trim() ? props.location : 'TBD';
  const isLocationTbd = locationLabel === 'TBD';

  const displayDuration = props.type === 'personal' ? props.completedDuration : props.plannedDuration;
  const displayDistance = props.type === 'personal' ? props.completedDistance : props.plannedDistance;

  const cardClasses = [
    'training-card-2',
    isPlanned ? 'planned' : 'completed',
    `viewer-${props.viewerType}`,
    props.isSelected ? 'selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="training-card-wrapper">
      <div className={cardClasses} onClick={props.onClick}>
        {/* Row 1: time + location pill */}
        <div className="card-top-row">
          <Typography className="card-time">{format(parseISO(props.time), 'h:mm a')}</Typography>
          <div className={`card-location-pill${isLocationTbd ? ' card-location-pill--tbd' : ''}`}>
            <Typography className="card-location-text">{locationLabel}</Typography>
          </div>
        </div>

        {/* Row 2: title */}
        <Typography className="card-title">{props.title}</Typography>

        {/* Row 3: team avatar + name  OR  athlete row (guardian) */}
        {props.viewerType === 'guardian' && props.athleteName ? (
          <div className="card-athlete-row">
            <div className="card-athlete-avatar">{props.athleteName[0].toUpperCase()}</div>
            <div className="card-athlete-info">
              <Typography className="card-athlete-name">{props.athleteName}</Typography>
              <div className="guardian-pill">
                <TripOriginIcon className="guardian-pill-icon" />
                <Typography className="guardian-pill-text">GUARDIAN</Typography>
              </div>
            </div>
          </div>
        ) : (
          <div className="card-team-row">
            <div className="card-team-avatar" style={{ backgroundColor: props.team?.color }}>
              {teamInitials(props.team?.name)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Typography className="card-team-name">
                {props.team?.name || props.athleteName || 'Individual Workout'}
              </Typography>
              {props.group?.name ? <Typography className="card-group-name">{props.group.name}</Typography> : null}
            </div>
          </div>
        )}

        {/* RSVP indicator — athlete viewer on planned trainings */}
        {props.viewerType === 'athlete' && isPlanned && props.rsvpStatus !== undefined && props.rsvpStatus !== null ? (
          <div>
            <Typography className={`rsvp-pill ${props.rsvpStatus ? 'rsvp-yes' : 'rsvp-no'}`}>
              {props.rsvpStatus ? 'Attending' : 'Not Attending'}
            </Typography>
          </div>
        ) : null}

        {/* Row 4: icon stats */}
        <div className="card-stats-icons-row">
          <AccessTimeIcon className="stat-icon" />
          <Typography className="stat-value">{formatDuration(displayDuration)}</Typography>
          <ShowChartIcon className="stat-icon" />
          <Typography className="stat-value">{formatDistance(displayDistance)}</Typography>
          {props.viewerType === 'coach' && (
            <>
              <PersonOutlineIcon className="stat-icon stat-icon--right" />
              <Typography className="stat-value">
                <span className="stat-completed">{props.team?.totalRsvp ?? 0}</span>
                {' /'}
                {props.team?.totalAssignees ?? 0}
              </Typography>
            </>
          )}
        </div>
      </div>

      {/* Comment badge */}
      {props.unreadCommentsCount !== undefined &&
        props.unreadCommentsCount !== null &&
        props.unreadCommentsCount > 0 && (
          <div className="comments-badge">
            <ChatBubbleOutlineIcon className="badge-icon" />
            <Typography className="badge-text">{props.unreadCommentsCount}</Typography>
          </div>
        )}
    </div>
  );
};

export default COETrainingCard;
